import React, { useState, useEffect } from "react";
import { apiService } from "../../utils/api";
import { FaEdit, FaTrash, FaSearch } from "react-icons/fa";

function BatchManagement() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBatch, setNewBatch] = useState({
    name: "",
    startTime: "",
    endTime: "",
    days: "",
    capacity: 30,
    description: "",
    teacherId: "",
  });

  const [batches, setBatches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");

  useEffect(() => {
    fetchBatches();
    fetchTeachers();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBatches();
      if (response.data.success) {
        setBatches(response.data.batches);
      } else {
        setError(response.data.message || "Failed to fetch batches");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await apiService.getTeachers();
      if (response.data.success) {
        setTeachers(response.data.teachers);
      } else {
        console.error("Failed to fetch teachers:", response.data.message);
      }
    } catch (err) {
      console.error("Error fetching teachers:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBatch((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!newBatch.name || !newBatch.startTime || !newBatch.endTime || !newBatch.days || !newBatch.teacherId) {
      setFormError("Name, start time, end time, days, and teacher are required");
      return;
    }

    try {
      setSubmitting(true);
      let response;

      if (isEditing) {
        response = await apiService.updateBatch(newBatch.id, newBatch);
      } else {
        response = await apiService.createBatch(newBatch);
      }

      if (response.data.success) {
        setFormSuccess(isEditing ? "Batch updated successfully!" : "Batch added successfully!");
        setNewBatch({
          name: "",
          startTime: "",
          endTime: "",
          days: "",
          capacity: 30,
          description: "",
          teacherId: "",
        });
        setShowAddForm(false);
        setIsEditing(false);
        fetchBatches();
      } else {
        setFormError(response.data.message || (isEditing ? "Failed to update batch" : "Failed to add batch"));
      }
    } catch (err) {
      setFormError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const showUpdateForm = (batch) => {
    setNewBatch({
      id: batch.id,
      name: batch.name,
      startTime: batch.startTime,
      endTime: batch.endTime,
      days: batch.days,
      capacity: batch.capacity,
      description: batch.description || "",
      teacherId: batch.teacherId,
      isActive: batch.isActive,
    });
    setIsEditing(true);
    setShowAddForm(true);
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm("Are you sure you want to delete this batch?")) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.deleteBatch(batchId);

      if (response.data.success) {
        setFormSuccess("Batch deleted successfully!");
        fetchBatches();
      } else {
        setError(response.data.message || "Failed to delete batch");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelForm = () => {
    setNewBatch({
      name: "",
      startTime: "",
      endTime: "",
      days: "",
      capacity: 30,
      description: "",
      teacherId: "",
    });
    setIsEditing(false);
    setShowAddForm(false);
    setFormError("");
    setFormSuccess("");
  };

  const formatDays = (days) => {
    return days.split(",").join(", ");
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";

    try {
      // Handle different time formats
      const timeParts = timeString.split(":");
      if (timeParts.length < 2) return timeString;

      const hours = parseInt(timeParts[0]);
      const minutes = timeParts[1];

      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;

      return `${displayHours}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  // Filter batches based on search term
  const filteredBatches = batches.filter((batch) => batch.name.toLowerCase().includes(searchTerm.toLowerCase()) || (batch.teacher && batch.teacher.username.toLowerCase().includes(searchTerm.toLowerCase())) || batch.days.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="batch-management">
      <div className="section-header">
        <h2>Batch Management</h2>
        <button
          className="add-button"
          onClick={() => {
            if (showAddForm) {
              cancelForm();
            } else {
              setShowAddForm(true);
            }
          }}
        >
          {showAddForm ? "Cancel" : "Add Batch"}
        </button>
      </div>

      {showAddForm && (
        <div className="add-batch-form">
          <h3>{isEditing ? "Edit Batch" : "Add New Batch"}</h3>
          {formError && <p className="error-message">{formError}</p>}
          {formSuccess && <p className="success-message">{formSuccess}</p>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Batch Name:</label>
              <input type="text" name="name" value={newBatch.name} onChange={handleInputChange} placeholder="Enter batch name" />
            </div>

            <div className="form-group">
              <label>Teacher:</label>
              <select name="teacherId" value={newBatch.teacherId} onChange={handleInputChange}>
                <option value="">Select a teacher</option>
                {teachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.username} ({teacher.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label>Start Time:</label>
                <input type="time" name="startTime" value={newBatch.startTime} onChange={handleInputChange} />
              </div>

              <div className="form-group half">
                <label>End Time:</label>
                <input type="time" name="endTime" value={newBatch.endTime} onChange={handleInputChange} />
              </div>
            </div>

            <div className="form-group">
              <label>Days:</label>
              <input type="text" name="days" value={newBatch.days} onChange={handleInputChange} placeholder="e.g., Mon,Wed,Fri" />
              <small>Enter days as comma-separated values (Mon,Tue,Wed,Thu,Fri,Sat,Sun)</small>
            </div>

            <div className="form-group">
              <label>Capacity:</label>
              <input type="number" name="capacity" value={newBatch.capacity} onChange={handleInputChange} min="1" />
            </div>

            <div className="form-group">
              <label>Description:</label>
              <textarea name="description" value={newBatch.description} onChange={handleInputChange} placeholder="Enter batch description" rows="3"></textarea>
            </div>

            {isEditing && (
              <div className="form-group">
                <label>Status:</label>
                <select name="isActive" value={newBatch.isActive} onChange={handleInputChange}>
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
            )}

            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? "Please wait..." : isEditing ? "Update Batch" : "Add Batch"}
            </button>
            &nbsp; &nbsp;
            <button type="button" className="cancel-button" onClick={cancelForm}>
              Cancel
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading batches...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button onClick={fetchBatches} className="retry-button">
            Try Again
          </button>
        </div>
      ) : (
        <div className="batches-list">
          <div className="list-header">
            <h3>Current Batches</h3>

            <div className="list-controls">
              <div className="search-container">
                <FaSearch className="search-icon" />
                <input type="text" placeholder="Search batches..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
              </div>

              <div className="view-toggle">
                <button className={`view-button ${viewMode === "table" ? "active" : ""}`} onClick={() => setViewMode("table")}>
                  Table
                </button>
                <button className={`view-button ${viewMode === "cards" ? "active" : ""}`} onClick={() => setViewMode("cards")}>
                  Cards
                </button>
              </div>
            </div>
          </div>

          {filteredBatches.length === 0 ? (
            <div className="empty-state">
              <p>No batches found.</p>
              {searchTerm && <p>Try adjusting your search criteria.</p>}
            </div>
          ) : viewMode === "table" ? (
            <div className="responsive-table-container">
              <table className="responsive-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Teacher</th>
                    <th>Schedule</th>
                    <th>Capacity</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBatches.map((batch) => (
                    <tr key={batch.id} className={batch.isActive ? "" : "inactive-row"}>
                      <td data-label="Name">{batch.name}</td>
                      <td data-label="Teacher">{batch.teacher ? batch.teacher.username : "No teacher assigned"}</td>
                      <td data-label="Schedule">
                        {formatTime(batch.startTime)} - {formatTime(batch.endTime)}
                        <br />
                        <span className="days-text">{formatDays(batch.days)}</span>
                      </td>
                      <td data-label="Capacity">
                        {batch.enrolledStudents ? batch.enrolledStudents.length : 0} / {batch.capacity}
                      </td>
                      <td data-label="Status">
                        <span className={`status-badge ${batch.isActive ? "active" : "inactive"}`}>{batch.isActive ? "Active" : "Inactive"}</span>
                      </td>
                      <td data-label="Actions" className="action-buttons">
                        <button onClick={() => showUpdateForm(batch)} className="action-button edit">
                          <FaEdit />
                          <span className="button-text">Edit</span>
                        </button> &nbsp; 
                        <button onClick={() => handleDeleteBatch(batch.id)} className="action-button delete">
                          <FaTrash />
                          <span className="button-text">Delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="batches-grid">
              {filteredBatches.map((batch) => (
                <div key={batch.id} className={`batch-card ${batch.isActive ? "" : "inactive-card"}`}>
                  <div className="card-header">
                    <h4 className="batch-name">{batch.name}</h4>
                    <span className={`status-badge ${batch.isActive ? "active" : "inactive"}`}>{batch.isActive ? "Active" : "Inactive"}</span>
                  </div>

                  <div className="card-body">
                    <div className="info-row">
                      <span className="info-label">Teacher:</span>
                      <span className="info-value">{batch.teacher ? batch.teacher.username : "No teacher assigned"}</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Schedule:</span>
                      <span className="info-value">
                        {formatTime(batch.startTime)} - {formatTime(batch.endTime)}
                      </span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Days:</span>
                      <span className="info-value">{formatDays(batch.days)}</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Capacity:</span>
                      <span className="info-value">
                        {batch.enrolledStudents ? batch.enrolledStudents.length : 0} / {batch.capacity}
                      </span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Description:</span>
                      <span className="info-value description">{batch.description}</span>
                    </div>
                  </div>

                  <div className="card-actions">
                    <button onClick={() => showUpdateForm(batch)} className="action-button edit">
                      <FaEdit />
                      Edit
                    </button>
                    <button onClick={() => handleDeleteBatch(batch.id)} className="action-button delete">
                      <FaTrash />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BatchManagement;
